/** @jsxImportSource @tiptap/core */
import { getHTMLFromFragment, mergeAttributes, Node, wrappingInputRule } from "@tiptap/core";
import { Fragment, type Node as ProseMirrorNode } from "@tiptap/pm/model";
import { defaultMarkdownSerializer, type MarkdownSerializerState } from "prosemirror-markdown";

export interface BlockquoteOptions {
    HTMLAttributes: Record<string, any>;
}

declare module "@tiptap/core" {
    interface Commands<ReturnType> {
        blockQuote: {
            setBlockquote: () => ReturnType;
            toggleBlockquote: () => ReturnType;
            unsetBlockquote: () => ReturnType;
        };
    }
}

const inputRegex = /^\s*>(?<theme>info|warning|warn|alert|danger)?\s$/;

export const Blockquote = Node.create<BlockquoteOptions>({
    name: "blockquote",
    content: "block+",
    group: "block",
    defining: true,
    addOptions() {
        return {
            HTMLAttributes: {},
        };
    },
    parseHTML() {
        return [{ tag: "blockquote" }];
    },
    addAttributes() {
        return {
            "data-theme": "ok",
            literal: {
                default: false,
                parseHTML: (element) => element.getAttribute("data-literal-quote") === "true",
                renderHTML: (attributes) => (attributes.literal ? { "data-literal-quote": "true" } : {}),
            },
        };
    },
    renderHTML(props) {
        const theme = props?.HTMLAttributes["data-theme"];
        const attrs = mergeAttributes(this.options.HTMLAttributes, props.HTMLAttributes);
        return (
            <blockquote {...attrs} data-theme={theme} data-callout={!!theme}>
                <slot />
            </blockquote>
        );
    },
    addStorage() {
        return {
            markdown: {
                serialize(
                    state: MarkdownSerializerState,
                    node: ProseMirrorNode,
                    parent: ProseMirrorNode,
                    index: number,
                ) {
                    // Markdown admonition syntax cannot express an intentionally literal marker.
                    if (
                        node.attrs.literal &&
                        /^\[!(NOTE|TIP|IMPORTANT|WARNING|CAUTION)\]/i.test(node.firstChild?.textContent ?? "")
                    ) {
                        state.write(getHTMLFromFragment(Fragment.from(node), node.type.schema));
                        state.closeBlock(node);
                    } else {
                        defaultMarkdownSerializer.nodes.blockquote!(state, node, parent, index);
                    }
                },
            },
        };
    },
    addCommands() {
        return {
            setBlockquote:
                () =>
                ({ commands }) => {
                    return commands.wrapIn(this.name);
                },
            toggleBlockquote:
                () =>
                ({ commands }) => {
                    return commands.toggleWrap(this.name);
                },
            unsetBlockquote:
                () =>
                ({ commands }) => {
                    return commands.lift(this.name);
                },
        };
    },
    addKeyboardShortcuts() {
        return {
            "Mod-Shift-b": () => this.editor.commands.toggleBlockquote(),
        };
    },
    addInputRules() {
        return [
            wrappingInputRule({
                type: this.type,
                find: inputRegex,
                keepAttributes: true,
                getAttributes: (m) => ({ "data-theme": m.groups?.theme }),
            }),
        ];
    },
});
